import React, { useEffect, useState } from 'react';
import {
  Box, Button, Stack, Typography,
} from '@worthy-npm/worthy-common-ui-components';
import { StepProps } from '../common';
import { Step, StepContainer, StepGridContainer } from '../../../styles/common';
import StepTitle from '../../stepTitle';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import {
  getShippingMethod,
  setShippingMethod,
} from '../../../slices/schedulingSlice';
import { SchedulingType } from '../../../types/scheduling';
import TrustSection from '../../scheduleShipping/TrustSection';
import { selectItemDescription, selectItemPublicId } from '../../../slices/submitSlice';
import WorthyAPI from '../../../services/worthyAPI';
import MethodSelection from '../../scheduleShipping/MethodSelection';
import { EXPERIMENTS, variantIsLoaded } from '../../../lib/experiment';
import GA from '../../../data/GA';

const textForButton = {
  [SchedulingType.PICKUP]: 'Continue with FedEx Pickup',
  [SchedulingType.DROPOFF]: 'Continue with FedEx Drop-Off',
};

function StepSchedulingType({
  next, prev, title, stepCaption, props, exit, stepName, active,
}: StepProps) {
  const shippingMethod = useAppSelector(getShippingMethod);
  const itemPublicId = useAppSelector(selectItemPublicId);
  const [value, setValue] = useState<SchedulingType>(shippingMethod);
  const dispatch = useAppDispatch();
  const itemDescription = useAppSelector(selectItemDescription);

  useEffect(() => {
    variantIsLoaded(EXPERIMENTS.scheduleInSubmission, ['1']);
  }, []);

  useEffect(() => {
    if (!active) return;
    GA.schedulingMethodSelectionStepView();
  }, [active, stepName]);

  useEffect(() => {
    if (itemPublicId) {
      WorthyAPI.logUserAction('Pickup/Drop-off modal presented', itemPublicId).catch((err) => {
        console.error(
          `Error logging Pickup/Drop-off modal presented user action for item ${itemPublicId}`,
          err,
        );
      });
    }
  }, [itemPublicId]);

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (value !== shippingMethod) {
      dispatch(setShippingMethod(value));
      WorthyAPI.logUserAction(`${value} radio button selected`, itemPublicId).catch((err) => {
        console.error(
          `Error logging ${value} radio button selected user action for item ${itemPublicId}`,
          err,
        );
      });
      WorthyAPI.sendShippingIntent(itemPublicId).catch((err) => {
        console.error(`Error sending shipping intent for item ${itemPublicId}`, err);
      });
    }
    GA.schedulingMethodSelectionStepNext(value);
    next(e);
  };

  return (
    <StepContainer data-automation="shipping-address-step">
      <Step>
        <StepTitle
          stepCaption={stepCaption}
          prev={prev}
          stepTitle={title || ''}
          stepNotice=""
          optionalProps={props || {}}
        />
        <StepGridContainer style={{ maxWidth: '560px' }}>
          <Stack gap={2} alignItems="center" justifyContent="center">
            <Stack gap={1} textAlign="center">
              <Typography variant="body1" color="secondary">
                Your:
                {' '}
                {itemDescription}
              </Typography>
              <Typography variant="body1">Choose your preferred shipping option.</Typography>
            </Stack>
            <MethodSelection method={value} setMethod={setValue} />
            <Stack gap={1} mt={5} maxWidth={320} width="100%">
              <Button
                variant="contained"
                color="highlight"
                fullWidth
                disableElevation
                onClick={handleClick}
              >
                {textForButton[value]}
              </Button>
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                onClick={() => {
                  GA.schedulingMethodSelectionStepSkip();
                  exit?.('final');
                }}
                sx={{
                  borderWidth: 2,
                  borderColor: 'primary.main',
                  '&:hover': {
                    borderWidth: 2,
                    borderColor: 'primary.main',
                  },
                }}
              >
                I&apos;ll schedule shipping later
              </Button>
            </Stack>
          </Stack>
          <Box mt={6}>
            <TrustSection />
          </Box>
        </StepGridContainer>
      </Step>
    </StepContainer>
  );
}

export default StepSchedulingType;
